/**
 * Splits text into runs so vocabulary words can be underlined. Pure.
 * A run is either plain text or a match of a glossary entry (matched on any
 * accepted form, whole word, case-insensitive; the original casing is kept).
 */
export interface GlossEntry {
  word: string;
  forms: string[];
  meaning: string;
}

export type TextRun = { kind: "text"; text: string } | { kind: "word"; text: string; entry: GlossEntry };

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function segmentText(text: string, glossary: GlossEntry[]): TextRun[] {
  if (!text) return [];
  const forms: { form: string; entry: GlossEntry }[] = [];
  for (const entry of glossary) {
    const all = new Set([entry.word, ...entry.forms].map((f) => f.toLowerCase()).filter(Boolean));
    for (const form of all) forms.push({ form, entry });
  }
  if (forms.length === 0) return [{ kind: "text", text }];
  // Longest forms first so "lanterns" wins over "lantern".
  forms.sort((a, b) => b.form.length - a.form.length);
  const re = new RegExp(`(?<![\\p{L}\\p{N}])(${forms.map((f) => escapeRe(f.form)).join("|")})(?![\\p{L}\\p{N}])`, "giu");
  const byForm = new Map(forms.map((f) => [f.form, f.entry]));
  const runs: TextRun[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) runs.push({ kind: "text", text: text.slice(last, start) });
    const entry = byForm.get(m[0].toLowerCase())!;
    runs.push({ kind: "word", text: m[0], entry });
    last = start + m[0].length;
  }
  if (last < text.length) runs.push({ kind: "text", text: text.slice(last) });
  return runs;
}
